// src/components/AuthForm.jsx  (or .tsx if you’re using TS)

import React, {
  useEffect,
  useState,
} from 'react';

import {
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import Logo from 'logo/Logo';
import { useNavigate } from 'react-router-dom';
import useBreakpoints from 'utils/useBreakpoints';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  TextField,
} from '@mui/material';

import {
  auth,
  createUserWithEmailAndPassword,
  db,
  googleProvider,
  signInWithEmailAndPassword,
} from '../firebaseConfig/firebaseConfig';

function AuthForm() {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();

  /* ───────────────────────────── state ────────────────────────────── */
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // simple invitation‑token gate
  const [token, setToken] = useState('');
  const [tokenSubmit, setTokenSubmit] = useState(false);
  const [tokenEnterCount, setTokenEnterCount] = useState(0);

  /* ───────────────────── helper: create / update docs ─────────────── */
  const upsertUserDocs = async (user) => {
    const userDocRef   = doc(db, 'users', user.uid);
    const chatsDocRef  = doc(db, 'userChats', user.uid);

    const [uSnap, cSnap] = await Promise.all([
      getDoc(userDocRef),
      getDoc(chatsDocRef),
    ]);

    if (!uSnap.exists() && user.emailVerified) {
      await setDoc(userDocRef, {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
      });
    }
    if (!cSnap.exists() && user.emailVerified) {
      await setDoc(chatsDocRef, {});
    }
  };

  /* ─────────────────────── email‑password flows ───────────────────── */
  const register = async (e) => {
    e.preventDefault();
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(user);
      alert('Verification email sent. Please check your inbox.');
      await signOut(auth);
    } catch (err) {
      setError('Failed to create an account. ' + err.message.split('Firebase:')[1]);
      handleForgotPassword();
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await upsertUserDocs(user);
      setShowForgotPassword(false);
      navigate('/');
    } catch (err) {
      setError('Failed to sign in. ' + err.message.split('Firebase:')[1]);
      setShowForgotPassword(true);
    }
  };

  /* ─────────────────────── Google sign‑in flow ─────────────────────── */
  const signInWithGoogle = async (e) => {
    e.preventDefault();
    try {
      if (isMobile) {
        // mobile browsers block pop‑ups; use redirect instead
        await signInWithRedirect(auth, googleProvider);

      } else {
        const { user } = await signInWithPopup(auth, googleProvider);
        await upsertUserDocs(user);
        navigate('/');
      }
    } catch (err) {
      setError('Failed to sign in with Google. ' + err.message.split('Firebase:')[1]);
    }
  };

  /* ─────────────────────── misc handlers ──────────────────────────── */
  const handleForgotPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset link sent. Please check your inbox.');
    } catch (err) {
      setError(err.message.split('Firebase:')[1]);
    }
  };

  const handlePassword = (e) => {
    setError('');
    setPassword(e.target.value);
  };
  const handleClickShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (e) => e.preventDefault();

  useEffect(() => {
    if (tokenEnterCount >= 5) alert('Too many attempts');
  }, [tokenEnterCount]);

  useEffect(() => {
    if (!isMobile) return;
  
    // once, on mount: did we just come back from a redirect?
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          upsertUserDocs(result.user).then(() => {
            navigate('/');
          });
        }
      })
      .catch((err) => {
        setError('Redirect login failed: ' + err.message.split('Firebase:')[1]);
      });
  }, [isMobile, navigate]);

  /* ─────────────────────────── JSX ────────────────────────────────── */
  return (
    <>
      {/* logo header on mobile */}
      {isMobile && (
        <div style={{ padding: '1rem' }}>
          <Logo color="black" height="30px" width="150px" type="long" />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: '0.4rem',
          height: 'auto',
          overflow: 'auto',
        }}
      >
        {/* token‑gate header */}
        {(token === 'ts@2024!' && tokenSubmit && !isMobile) ? (
          <div style={{ paddingBottom: '2rem' }}>
            <Logo color="black" height="30px" width="150px" type="long" />
          </div>
        ) : (
          <>
            <h2>Welcome to Autonomy!</h2>
            <h4
              style={{
                color: 'gray',
                textAlign: 'center',
                marginBottom: '2rem',
              }}
            >
              Access is invitation‑only at this time. We will be live by Q2 2025.
              Stay tuned!
            </h4>
          </>
        )}

        {/* token form */}
        {(token !== 'ts@2024!' && !tokenSubmit && tokenEnterCount >= 5) && (
          <Alert severity="warning">Too many attempts. Try again later.</Alert>
        )}

        {(!tokenSubmit && tokenEnterCount < 5) && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setTokenEnterCount((c) => c + 1);
              token === 'ts@2024!'
                ? setTokenSubmit(true)
                : alert('Invalid Token');
            }}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '1rem',
            }}
          >
            <TextField
              required
              sx={{ width: '100%' }}
              variant="outlined"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Token"
            />
            <Button sx={{ background: 'black' }} variant="contained" type="submit">
              Submit Token
            </Button>
          </form>
        )}

        {/* full auth form (only when token is valid) */}
        {token === 'ts@2024!' && tokenSubmit && (
          <>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {showForgotPassword && (
              <button style={{ color: 'red' }} onClick={handleForgotPassword}>
                Forgot Password?
              </button>
            )}

            <form
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  width: '80%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                }}
              >
                <TextField
                  sx={{ width: '100%' }}
                  variant="standard"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                />

                <FormControl
                  variant="standard"
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <Input
                    required
                    value={password}
                    onChange={handlePassword}
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Password"
                  />
                </FormControl>

                <div
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginTop: '2rem',
                    marginBottom: '1rem',
                  }}
                >
                  <Button
                    sx={{ color: 'black' }}
                    variant="outlined"
                    onClick={register}
                  >
                    Register
                  </Button>
                  <Button
                    sx={{ background: 'black' }}
                    variant="contained"
                    onClick={login}
                  >
                    Login
                  </Button>
                </div>

                <button className="btn" onClick={signInWithGoogle}>
                  <img
                    src="/GoogleIcon.png"
                    alt=""
                    style={{ height: 25, width: 25, marginRight: 8 }}
                  />
                  Login with Google
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}

export default AuthForm;
